import React, { useState, useEffect } from 'react';
import {
  Car,
  Plus,
  Edit,
  X,
  Search,
  RefreshCw,
  Save,
  MapPin,
  AlertCircle
} from 'lucide-react';
import AdminAPI from '../services/api';
import { VehicleType, State, VehicleTypeForm } from '../types';

const VehicleTypes: React.FC = () => {
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(false);
  const [statesLoading, setStatesLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<VehicleType | null>(null);
  const [search, setSearch] = useState('');
  const [selectedState, setSelectedState] = useState<string>('');

  // Load states on component mount
  useEffect(() => {
    loadStates();
  }, []);

  // Load vehicle types when state is selected
  useEffect(() => {
    if (selectedState) {
      loadVehicleTypes();
    } else {
      setVehicleTypes([]);
    }
  }, [selectedState]);

  const loadStates = async () => {
    try {
      setStatesLoading(true);
      const statesData = await AdminAPI.getAllStates();
      setStates(statesData);
    } catch (err) {
      console.error('Error loading states:', err);
    } finally {
      setStatesLoading(false);
    }
  };

  const loadVehicleTypes = async () => {
    if (!selectedState) return;
    
    try {
      setLoading(true);
      const vehicleTypesData = await AdminAPI.getVehicleTypes(selectedState);
      setVehicleTypes(vehicleTypesData);
    } catch (err) {
      console.error('Error loading vehicle types:', err);
      setVehicleTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data: VehicleTypeForm) => {
    try {
      if (editItem) {
        await AdminAPI.updateVehicleType(editItem._id, data);
      } else {
        await AdminAPI.createVehicleType(data);
      }
      await loadVehicleTypes();
      setEditModalOpen(false);
      setEditItem(null);
    } catch (err) {
      console.error('Error saving vehicle type:', err);
      alert('Failed to save vehicle type');
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await AdminAPI.toggleVehicleType(id);
      await loadVehicleTypes();
    } catch (err) {
      console.error('Error toggling vehicle type:', err);
      alert('Failed to toggle vehicle type status');
    }
  };

  const filteredVehicleTypes = vehicleTypes.filter(vehicleType =>
    vehicleType.name.toLowerCase().includes(search.toLowerCase())
  );

  const VehicleTypeModal: React.FC<{ 
    vehicleType?: VehicleType; 
    onClose: () => void; 
    onSave: (data: VehicleTypeForm) => void 
  }> = ({ vehicleType, onClose, onSave }) => {
    const [formData, setFormData] = useState<VehicleTypeForm>({
      name: vehicleType?.name || '',
      state_id: vehicleType?.state_id || selectedState,
      is_active: vehicleType?.is_active !== false
    });

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!formData.name.trim() || !formData.state_id) {
        alert('Vehicle type name and state are required');
        return;
      }
      onSave(formData);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {vehicleType ? 'Edit Vehicle Type' : 'Add New Vehicle Type'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Vehicle Type Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter vehicle type name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">State</label>
              <select
                value={formData.state_id}
                onChange={(e) => setFormData(prev => ({ ...prev, state_id: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              >
                <option value="">Select a state</option>
                {states.map(state => (
                  <option key={state._id} value={state._id}>{state.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Vehicle Types Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage vehicle types and their configurations for different states.
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            onClick={loadVehicleTypes}
            disabled={!selectedState}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={() => {
              setEditItem(null);
              setEditModalOpen(true);
            }}
            disabled={!selectedState}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Vehicle Type
          </button>
        </div>
      </div>

      {/* State Selection - Prominent */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <MapPin className="h-5 w-5 text-blue-600 mr-2" />
          <h3 className="text-lg font-medium text-blue-900">Select State</h3>
        </div>
        <p className="text-sm text-blue-700 mb-4">
          Choose a state to view and manage its vehicle types. Vehicle types are state-specific.
        </p>
        <div className="max-w-md">
          <select
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base"
            disabled={statesLoading}
          >
            <option value="">
              {statesLoading ? 'Loading states...' : 'Select a state to view vehicle types'}
            </option>
            {states.map(state => (
              <option key={state._id} value={state._id}>
                {state.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Show content only when state is selected */}
      {!selectedState ? (
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No State Selected</h3>
          <p className="text-gray-500">
            Please select a state from the dropdown above to view and manage vehicle types for that state.
          </p>
        </div>
      ) : (
        <>
          {/* Search Filter */}
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search vehicle types..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Car className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500">Total Vehicle Types</h3>
                  <p className="text-2xl font-bold text-gray-900">{vehicleTypes.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <MapPin className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500">Active Types</h3>
                  <p className="text-2xl font-bold text-gray-900">
                    {vehicleTypes.filter(vt => vt.is_active).length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <X className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500">Inactive Types</h3>
                  <p className="text-2xl font-bold text-gray-900">
                    {vehicleTypes.filter(vt => !vt.is_active).length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Vehicle Types Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Vehicle Types for {states.find(s => s._id === selectedState)?.name}
              </h3>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vehicle Type Name
                  </th>
                
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded"></div></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded"></div></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded"></div></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded"></div></td>
                      <td className="px-6 py-4 whitespace-nowrap"><div className="h-4 bg-gray-200 rounded"></div></td>
                    </tr>
                  ))
                ) : filteredVehicleTypes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      No vehicle types found for {states.find(s => s._id === selectedState)?.name}
                    </td>
                  </tr>
                ) : (
                  filteredVehicleTypes.map((vehicleType) => (
                    <tr key={vehicleType._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {vehicleType.name}
                      </td>
                     
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          vehicleType.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {vehicleType.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(vehicleType.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setEditItem(vehicleType);
                              setEditModalOpen(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggle(vehicleType._id)}
                            className={vehicleType.is_active ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"}
                            title={vehicleType.is_active ? "Deactivate" : "Activate"}
                          >
                            {vehicleType.is_active ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modal */}
      {editModalOpen && (
        <VehicleTypeModal
          vehicleType={editItem || undefined}
          onClose={() => {
            setEditModalOpen(false);
            setEditItem(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default VehicleTypes; 